/**
 * Pure product-grouping helpers for the PUBLIC marketing site.
 *
 * Kept standalone (no Ant Design imports) so the public bundle stays lean and the
 * public pages never pull authenticated back-office screens into their chunk.
 *
 * A "group" is a product family: every variant sharing a `groupCode` collapses
 * into one catalogue card. The group key (used in the public URL) is the
 * groupCode, falling back to the SKU, falling back to the id.
 */

import { fmtUom } from '../lib/format';

export interface PublicProduct {
  id: string;
  sku: string;
  groupCode?: string | null;
  variantLabel?: string | null;
  name: string;
  hsnCode?: string | null;
  uomCode: string;
  packSize: number;
  moq: number;
  mrp: number;
  imageUrl?: string | null;
  imageUrls?: string[] | null;
  videoUrl?: string | null;
  videoUrls?: string[] | null;
  description?: string | null;
  isActive?: boolean;
}

export interface ProductGroup {
  key: string;
  variants: PublicProduct[];
  rep: PublicProduct;
  baseName: string;
}

export function productGroupKey(p: PublicProduct): string {
  return p.groupCode || p.sku || String(p.id);
}

function commonPrefix(strs: string[]): string {
  if (!strs.length) return '';
  let pre = strs[0];
  for (const s of strs.slice(1)) {
    let i = 0;
    while (i < pre.length && i < s.length && pre[i] === s[i]) i++;
    pre = pre.slice(0, i);
  }
  return pre;
}

export function groupBaseName(variants: PublicProduct[]): string {
  const names = variants.map((v) => v.name).filter(Boolean);
  if (names.length <= 1) return names[0] ?? variants[0]?.sku ?? 'Product';
  const pre = commonPrefix(names).replace(/[\s\-–·,:]+$/, '').trim();
  return pre.length >= 3 ? pre : names[0];
}

export function variantTag(v: PublicProduct): string {
  // Prefer the UOM-friendly pack size derived from the numbers (0.25 LTR → "250 ml")
  // over the stored free-text label, so the size always reads consistently.
  if (Number(v.packSize) > 0 && v.uomCode) return fmtUom(v.packSize, v.uomCode);
  if (v.variantLabel) return v.variantLabel;
  return v.uomCode || v.sku;
}

/** Collapse a flat product list into one entry per family, active only. */
export function toGroups(products: PublicProduct[]): ProductGroup[] {
  const map = new Map<string, PublicProduct[]>();
  for (const p of products) {
    if (p.isActive === false) continue;
    const key = productGroupKey(p);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  return Array.from(map.entries()).map(([key, variants]) => ({
    key,
    variants,
    rep: variants[0],
    baseName: groupBaseName(variants),
  }));
}
